import React, { useMemo, useState } from 'react';
import { Trash2, Plus } from 'lucide-react';

// Réglage des options (refonte du 2026-10-27, maquette validée par Emeni) : une option se propose sur une prestation quand le pourcentage de
// son groupe est supérieur à 0 et qu'elle n'en est pas exclue. Deux vues sur la même configuration : par prestation, ou par option.
// Les prix fixes d'avant (config.options[].prix) ne se saisissent plus ici ; ceux qui existent encore sont signalés et retirables.

export interface OptionTarif { item_id: string; taux: Record<string, number>; prix?: Record<string, number>; duree?: Record<string, number>; exclu?: Record<string, boolean> }
export interface ConfigOptions { groupes?: Record<string, string[]>; options?: OptionTarif[]; durees?: Record<string, number>; [k: string]: any }
export interface ItemCatalogue { id: string; label: string; unit_price_ttc: number; unit_type: string; default_duration_minutes?: number; kind: 'prestation' | 'option' }

export const GROUPES: [string, string][] = [['assise', 'Assises'], ['canape', 'Canapés'], ['literie', 'Literie'], ['tapis', 'Tapis / moquette']];
const LABEL_GROUPE: Record<string, string> = Object.fromEntries(GROUPES);

// Même repli que le serveur (functions/api/modules/groupesPrestations.js) : liste de la configuration, sinon nom de la prestation.
const MOTIFS: [string, RegExp][] = [
    ['literie', /matelas|t[êe]te de lit|sommier|oreiller/i],
    ['canape', /canap|sofa|clic[- ]?clac/i],
    ['tapis', /moquette|tapis|carpette/i],
    ['assise', /chaise|fauteuil|banquette|tabouret|pouf/i],
];
export const groupeDe = (config: ConfigOptions, id: string, label: string): string | null => {
    for (const [g, ids] of Object.entries(config.groupes || {})) if ((ids || []).map(String).includes(String(id))) return g;
    for (const [g, motif] of MOTIFS) if (motif.test(label)) return g;
    return null;
};

const INPUT = 'bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm font-bold text-content outline-none focus:border-brand-500 transition-colors text-right';
const LABEL = 'text-[10px] font-bold text-content-muted uppercase tracking-widest';
const eur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const num = (v: string) => (v === '' ? 0 : Number(v.replace(',', '.')) || 0);
const unite = (t: string) => (t === 'm2' ? 'm²' : t === 'metre_lineaire' ? 'ml' : 'unité');

function Interrupteur({ actif, onClick, disabled, label }: { actif: boolean; onClick: () => void; disabled?: boolean; label: string }) {
    return (
        <button type="button" role="switch" aria-checked={actif} aria-label={label} disabled={disabled} onClick={onClick}
            className={`relative w-[46px] h-[28px] rounded-full shrink-0 transition-colors disabled:opacity-40 ${actif ? 'bg-brand-500' : 'bg-border'}`}>
            <span className={`absolute top-[3px] w-[22px] h-[22px] rounded-full bg-white shadow transition-all ${actif ? 'left-[21px]' : 'left-[3px]'}`} />
        </button>
    );
}

export function OptionsConfigurateur({ config, catalogue, maj, prive }: { config: ConfigOptions; catalogue: ItemCatalogue[]; maj: (fn: (c: ConfigOptions) => ConfigOptions) => void; prive: boolean }) {
    const [vue, setVue] = useState<'presta' | 'option'>('presta');
    const [selP, setSelP] = useState<string>('');
    const [selO, setSelO] = useState<string>('');
    const [aAjouter, setAAjouter] = useState('');
    const prestations = useMemo(() => catalogue.filter((i) => i.kind === 'prestation'), [catalogue]);
    const optionsCatalogue = useMemo(() => catalogue.filter((i) => i.kind === 'option'), [catalogue]);
    const opts = config.options || [];
    const nomDe = (id: string) => catalogue.find((i) => i.id === id)?.label || id;
    const itemOption = (id: string) => catalogue.find((i) => i.id === id);
    const prestaCourante = prestations.find((p) => p.id === selP) || prestations[0];
    const optionCourante = opts.find((o) => o.item_id === selO) || opts[0];
    const groupeDuP = (p: ItemCatalogue) => groupeDe(config, p.id, p.label);
    const tauxDe = (o: OptionTarif, g: string | null) => (g ? Number(o.taux?.[g]) || 0 : 0);
    const prixFixeDe = (o: OptionTarif, pid: string) => (o.prix && o.prix[pid] !== undefined && (o.prix[pid] as unknown) !== '' ? Number(o.prix[pid]) : null);
    const propose = (o: OptionTarif, p: ItemCatalogue) => !o.exclu?.[p.id] && (tauxDe(o, groupeDuP(p)) > 0 || prixFixeDe(o, p.id) !== null);

    const idxOption = (id: string) => (config.options || []).findIndex((o) => o.item_id === id);
    const basculer = (oid: string, pid: string) => maj((c) => {
        const o = c.options![idxOption(oid)];
        o.exclu = o.exclu || {};
        if (o.exclu[pid]) delete o.exclu[pid]; else o.exclu[pid] = true;
        return c;
    });
    const posePct = (oid: string, g: string, v: string) => maj((c) => {
        const o = c.options![idxOption(oid)];
        if (v === '') delete o.taux[g]; else o.taux[g] = num(v);
        return c;
    });
    const poseDureeOpt = (oid: string, pid: string, v: string) => maj((c) => {
        const o = c.options![idxOption(oid)];
        o.duree = o.duree || {};
        if (v === '') delete o.duree[pid]; else o.duree[pid] = Math.max(0, Math.round(num(v)));
        return c;
    });
    const poseDureePresta = (pid: string, v: string) => maj((c) => {
        c.durees = c.durees || {};
        if (v === '') delete c.durees[pid]; else c.durees[pid] = Math.max(0, Math.round(num(v)));
        return c;
    });
    const retirePrixFixe = (oid: string, pid: string) => maj((c) => { const o = c.options![idxOption(oid)]; if (o.prix) delete o.prix[pid]; return c; });
    const retireOption = (oid: string) => maj((c) => { c.options = (c.options || []).filter((o) => o.item_id !== oid); return c; });
    const ajouteOption = () => {
        if (!aAjouter) return;
        maj((c) => { c.options = c.options || []; c.options.push({ item_id: aAjouter, taux: {}, prix: {} }); return c; });
        setSelO(aAjouter); setAAjouter('');
    };

    const dispo = optionsCatalogue.filter((o) => !opts.some((x) => x.item_id === o.id));
    const nbPrixFixes = opts.reduce((n, o) => n + Object.keys(o.prix || {}).filter((k) => prixFixeDe(o, k) !== null).length, 0);

    const ligneOption = (o: OptionTarif, p: ItemCatalogue) => {
        const g = groupeDuP(p);
        const taux = tauxDe(o, g);
        const fixe = prixFixeDe(o, p.id);
        const on = propose(o, p);
        const bloque = !on && taux === 0 && fixe === null;
        const catalogueDuree = itemOption(o.item_id)?.default_duration_minutes;
        return (
            <div key={o.item_id} className="py-3 border-b border-border last:border-b-0">
                <div className="flex items-start gap-3 flex-wrap">
                    <Interrupteur actif={on} disabled={bloque} onClick={() => basculer(o.item_id, p.id)} label={`Proposer ${nomDe(o.item_id)} sur ${p.label}`} />
                    <div className="flex-1 min-w-[12rem]">
                        <p className={`text-sm font-bold ${on ? 'text-content' : 'text-content-muted line-through'}`}>{nomDe(o.item_id)}</p>
                        <p className="text-xs text-content-muted">
                            {on ? 'Proposée sur cette prestation' : bloque ? (g ? 'Pourcentage à 0 pour ce groupe : réglez-le dans la vue « Par option »' : 'Cette prestation n\'est dans aucun groupe') : 'Non proposée sur cette prestation'}
                        </p>
                    </div>
                    {on && (
                        <div className="flex items-center gap-3 flex-wrap">
                            {fixe === null ? (
                                <>
                                    <label className="flex items-center gap-1.5">
                                        <input className={INPUT + ' w-[72px]'} inputMode="decimal" aria-label={`Pourcentage de ${nomDe(o.item_id)}`} value={g ? (o.taux[g] ?? '') : ''} placeholder="0"
                                            onChange={(e) => g && posePct(o.item_id, g, e.target.value)} />
                                        <span className="text-xs text-content-muted">%</span>
                                    </label>
                                    <span className="text-sm font-black text-content min-w-[70px]">{eur(p.unit_price_ttc * taux / 100)}</span>
                                </>
                            ) : (
                                <span className="text-xs text-content-muted inline-flex items-center gap-2">
                                    <strong className="text-content">Prix fixe {eur(fixe)}</strong> (remplace le pourcentage)
                                    <button type="button" className="underline font-bold text-content" onClick={() => retirePrixFixe(o.item_id, p.id)}>Retirer</button>
                                </span>
                            )}
                            <label className="flex items-center gap-1.5">
                                <input className={INPUT + ' w-[64px]'} inputMode="numeric" aria-label={`Temps de ${nomDe(o.item_id)} sur ${p.label}, en minutes`} value={o.duree?.[p.id] ?? ''} placeholder={catalogueDuree ? String(catalogueDuree) : '—'}
                                    onChange={(e) => poseDureeOpt(o.item_id, p.id, e.target.value)} />
                                <span className="text-xs text-content-muted">min</span>
                            </label>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const vuePrestation = () => {
        if (!prestaCourante) return <p className="text-sm text-content-muted italic">Aucune prestation dans le catalogue {prive ? 'des particuliers' : 'des professionnels'}.</p>;
        const p = prestaCourante;
        const nb = opts.filter((o) => propose(o, p)).length;
        const parGroupe = [...GROUPES.map(([k]) => k), '__autre'].map((k) => ({ k, items: prestations.filter((x) => (groupeDuP(x) || '__autre') === k) })).filter((x) => x.items.length);
        return (
            <>
                <div className="rounded-xl border border-border bg-surface p-3 space-y-1">
                    {parGroupe.map(({ k, items }) => (
                        <div key={k}>
                            <p className={LABEL + ' mt-2 mb-1.5'}>{LABEL_GROUPE[k] || 'Autres prestations'}</p>
                            <div className="flex flex-wrap gap-2">
                                {items.map((x) => (
                                    <button key={x.id} type="button" onClick={() => setSelP(x.id)} aria-pressed={x.id === p.id}
                                        className={`px-3 py-2 rounded-full border text-xs font-semibold min-h-[40px] ${x.id === p.id ? 'bg-brand-500/15 border-brand-500 text-content' : 'border-border bg-surface text-content'}`}>
                                        {x.label} <span className="text-content-muted ml-1">{x.unit_price_ttc.toFixed(2).replace('.', ',')} €</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap border-b border-border pb-3">
                        <div>
                            <h4 className="text-base font-black text-content">{p.label}</h4>
                            <p className="text-xs text-content-muted">{eur(p.unit_price_ttc)} par {unite(p.unit_type)} · groupe « {LABEL_GROUPE[groupeDuP(p) || ''] || 'aucun'} » · <strong>{nb}</strong> option{nb > 1 ? 's' : ''} proposée{nb > 1 ? 's' : ''}</p>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-content-muted">Temps de nettoyage
                            <input className={INPUT + ' w-[72px]'} inputMode="numeric" aria-label={`Temps de ${p.label} par unité, en minutes`} value={config.durees?.[p.id] ?? ''} placeholder={p.default_duration_minutes ? String(p.default_duration_minutes) : '—'}
                                onChange={(e) => poseDureePresta(p.id, e.target.value)} />
                            min / {unite(p.unit_type)}
                        </label>
                    </div>
                    {opts.length === 0 && <p className="text-sm text-content-muted italic py-3">Aucune option réglée : ajoutez-en une ci-dessous.</p>}
                    {opts.map((o) => ligneOption(o, p))}
                    <p className="text-[11px] text-content-muted mt-3">Le pourcentage est partagé par tout le groupe « {LABEL_GROUPE[groupeDuP(p) || ''] || '—'} » : le modifier ici le change pour toutes ses prestations. Pour retirer une option d'une seule prestation, utilisez l'interrupteur.</p>
                </div>
            </>
        );
    };

    const vueOption = () => {
        if (!optionCourante) return <p className="text-sm text-content-muted italic">Aucune option réglée : ajoutez-en une ci-dessous.</p>;
        const o = optionCourante;
        const nb = prestations.filter((p) => propose(o, p)).length;
        return (
            <>
                <div className="rounded-xl border border-border bg-surface p-3 flex flex-wrap gap-2">
                    {opts.map((x) => (
                        <button key={x.item_id} type="button" onClick={() => setSelO(x.item_id)} aria-pressed={x.item_id === o.item_id}
                            className={`px-3 py-2 rounded-full border text-xs font-semibold min-h-[40px] ${x.item_id === o.item_id ? 'bg-brand-500/15 border-brand-500 text-content' : 'border-border bg-surface text-content'}`}>
                            {nomDe(x.item_id)}
                        </button>
                    ))}
                </div>
                <div className="rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap border-b border-border pb-3">
                        <div>
                            <h4 className="text-base font-black text-content">{nomDe(o.item_id)}</h4>
                            <p className="text-xs text-content-muted">Proposée sur <strong>{nb}</strong> prestation{nb > 1 ? 's' : ''} sur {prestations.length}</p>
                        </div>
                        <button type="button" onClick={() => { retireOption(o.item_id); setSelO(''); }} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-500 min-h-[36px]">
                            <Trash2 size={13} /> Retirer cette option des réglages
                        </button>
                    </div>
                    <p className={LABEL + ' mt-3'}>Pourcentage du prix de la prestation, par groupe</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 my-2.5">
                        {GROUPES.map(([k, l]) => (
                            <div key={k} className="rounded-lg border border-border p-2.5">
                                <p className="text-[11px] font-bold text-content-muted mb-1.5">{l}</p>
                                <label className="flex items-center gap-1.5">
                                    <input className={INPUT + ' w-full'} inputMode="decimal" aria-label={`${nomDe(o.item_id)}, ${l}, en %`} value={o.taux?.[k] ?? ''} placeholder="0" onChange={(e) => posePct(o.item_id, k, e.target.value)} />
                                    <span className="text-xs text-content-muted">%</span>
                                </label>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-content-muted">0 % ou vide : l'option n'est pas proposée pour ce groupe. Ensuite, retirez-la d'une prestation précise avec son interrupteur.</p>
                    {[...GROUPES.map(([k]) => k), '__autre'].map((k) => {
                        const liste = prestations.filter((p) => (groupeDuP(p) || '__autre') === k);
                        if (!liste.length) return null;
                        return (
                            <div key={k}>
                                <p className={LABEL + ' mt-4 mb-1'}>{LABEL_GROUPE[k] || 'Autres prestations'}</p>
                                {liste.map((p) => {
                                    const g = groupeDuP(p); const taux = tauxDe(o, g); const on = propose(o, p); const fixe = prixFixeDe(o, p.id);
                                    const bloque = !on && taux === 0 && fixe === null;
                                    return (
                                        <div key={p.id} className="flex items-center gap-3 flex-wrap py-2.5 border-b border-border last:border-b-0">
                                            <Interrupteur actif={on} disabled={bloque} onClick={() => basculer(o.item_id, p.id)} label={`Proposer sur ${p.label}`} />
                                            <div className="flex-1 min-w-[10rem]">
                                                <p className={`text-sm font-bold ${on ? 'text-content' : 'text-content-muted line-through'}`}>{p.label}</p>
                                                <p className="text-xs text-content-muted">{eur(p.unit_price_ttc)} · temps de nettoyage {config.durees?.[p.id] ?? p.default_duration_minutes ?? '—'} min{fixe !== null ? ` · prix fixe ${eur(fixe)}` : ''}</p>
                                            </div>
                                            {on && (
                                                <>
                                                    <span className="text-sm font-black text-content min-w-[70px]">{eur(fixe !== null ? fixe : p.unit_price_ttc * taux / 100)}</span>
                                                    <label className="flex items-center gap-1.5">
                                                        <input className={INPUT + ' w-[64px]'} inputMode="numeric" aria-label={`Temps de ${nomDe(o.item_id)} sur ${p.label}, en minutes`} value={o.duree?.[p.id] ?? ''} placeholder={itemOption(o.item_id)?.default_duration_minutes ? String(itemOption(o.item_id)?.default_duration_minutes) : '—'}
                                                            onChange={(e) => poseDureeOpt(o.item_id, p.id, e.target.value)} />
                                                        <span className="text-xs text-content-muted">min</span>
                                                    </label>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <section className="space-y-3">
            <div className="rounded-xl border border-border bg-brand-500/10 p-3.5">
                <p className="text-sm font-bold text-content">Comment ça marche</p>
                <p className="text-xs text-content-muted mt-1">Le prix d'une option = un pourcentage du prix de la prestation. Vous réglez, pour chaque prestation, <strong>quelles options sont proposées</strong>, <strong>le pourcentage</strong> et <strong>le temps</strong>. Le client ne voit jamais ces chiffres.</p>
            </div>
            <h3 className="text-base font-black text-content pt-2">Options</h3>
            <div className="inline-flex gap-0.5 p-[3px] rounded-[10px] bg-surface-soft border border-border" role="tablist">
                {([['presta', 'Par prestation'], ['option', 'Par option']] as const).map(([k, l]) => (
                    <button key={k} type="button" role="tab" aria-selected={vue === k} onClick={() => setVue(k)}
                        className={`px-3.5 py-2 rounded-[7px] text-[13px] font-bold min-h-[36px] ${vue === k ? 'bg-surface text-content shadow-sm' : 'text-content-muted'}`}>{l}</button>
                ))}
            </div>
            {vue === 'presta' ? vuePrestation() : vueOption()}
            <div className="flex items-center gap-2 flex-wrap">
                {dispo.length > 0 ? (
                    <>
                        <select className={INPUT + ' text-left font-semibold'} value={aAjouter} onChange={(e) => setAAjouter(e.target.value)} aria-label="Option du catalogue à ajouter">
                            <option value="">+ Ajouter une option du catalogue…</option>
                            {dispo.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                        <button type="button" disabled={!aAjouter} onClick={ajouteOption} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-500 text-white text-xs font-bold disabled:opacity-50 min-h-[36px]"><Plus size={13} /> Ajouter</button>
                    </>
                ) : opts.length === 0 ? <span className="text-xs text-content-muted italic">Aucune option dans le catalogue {prive ? 'des particuliers' : 'des professionnels'}.</span> : null}
            </div>
            {nbPrixFixes > 0 && (
                <div className="rounded-xl border border-border bg-surface p-3 text-xs text-content-muted flex flex-wrap items-center gap-3" role="status">
                    <span className="flex-1 min-w-[12rem]"><strong className="text-content">{nbPrixFixes} prix fixe{nbPrixFixes > 1 ? 's' : ''} enregistré{nbPrixFixes > 1 ? 's' : ''} avant la refonte.</strong> Ils priment encore sur le pourcentage pour leur prestation et sont signalés sur sa fiche.</span>
                    <button type="button" className="px-2.5 py-1.5 rounded-lg border border-border font-bold text-content hover:border-brand-500 min-h-[36px]"
                        onClick={() => maj((c) => { (c.options || []).forEach((o) => { o.prix = {}; }); return c; })}>Tous les retirer</button>
                </div>
            )}
        </section>
    );
}
